import { useCallback, useEffect, useMemo, useState } from "react";
import { useLoaderData } from "react-router-dom";

import { inject } from "../../../../../../../../lib/itn-ui-kit/services/service-container";
import useService from "../../../../../../../hooks/useService";
import InlineEditRow from "../components/InlineEditRow";

export default function BasicInfoSection({ caseObj }) {
  const { userData } = useLoaderData();
  const currentUserService = inject("current-user-service");
  const { userRoles } = currentUserService;
  const enumRepository = useService("repositories/enum-repository");
  const fieldOptionUserRepository = useService("repositories/field-option-user-repository");
  const [userValueLabels, setUserValueLabels] = useState([]);
  const [languageLabels, setLanguageLabels] = useState([]);

  function getAge(dateString) {
    const today = new Date();
    const birthDate = new Date(dateString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  async function fetchOptions() {
    try {
      const userLabels = await fieldOptionUserRepository.query({});
      setUserValueLabels(userLabels || []);
      const languageResp = await enumRepository.query({
        urlPath: "utilities/language/?ordering=created_at&limit=100",
      });
      setLanguageLabels(languageResp || []);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    fetchOptions();
  }, []);

  const inputs = useMemo(
    () => [
      {
        group: [
          {
            keyVal: "namePrefix",
            type: "select",
            title: "Prefix",
            optionsUrl: "field_options/?content_type=user&field_name=name_prefix&ordering=order&",
            nullable: true,
            displayNameKey: "label",
          },
          {
            keyVal: "gender",
            type: "select",
            title: "Gender",
            optionsUrl: "field_options/?content_type=user&field_name=gender&ordering=order&",
            nullable: true,
            displayNameKey: "label",
          },
        ],
      },
      {
        group: [
          {
            keyVal: "name",
            type: "text",
            title: "Client Name",
          },
          {
            keyVal: "maritalStatus",
            type: "select",
            title: "Marital Status",
            optionsUrl:
              "field_options/?content_type=user&field_name=marital_status&ordering=order&",
            nullable: true,
            displayNameKey: "label",
          },
        ],
      },
      {
        group: [
          {
            keyVal: "dateOfBirth",
            type: "date",
            title: "Date of Birth",
          },
          {
            keyVal: "age",
            type: "text",
            title: "Client Age",
            disabled: true,
            staticVal: userData?.dateOfBirth ? getAge(userData.dateOfBirth) : 0,
          },
        ],
      },
      {
        group: [
          {
            keyVal: "preferredLanguage",
            type: "search-select",
            title: "Preferred Language",
            optionsUrl: "utilities/language/?ordering=created_at&limit=1000",
            customLimit: "25",
            displayNameKey: "description",
            customValueLabels: languageLabels,
          },
          {
            keyVal: "spokenLanguage",
            type: "multi-search-select",
            title: "Spoken Languages",
            optionsUrl: "utilities/language/?ordering=created_at&limit=1000",
            customLimit: "25",
            displayNameKey: "description",
            customValueLabels: languageLabels,
          },
        ],
      },
    ],
    [languageLabels, userData]
  );

  const renderFields = useCallback(
    () =>
      inputs.map((key, idx) => (
        <div key={idx} className="flex w-full items-baseline">
          {key.group.map((subKey, subItemIdx) => (
            <div
              key={subKey.keyVal ?? subItemIdx}
              className={key.group.length <= 1 ? "w-full" : `w-1/${key.group.length} mx-4 my-2`}
            >
              <InlineEditRow
                key={subKey.keyVal ?? subItemIdx}
                valueLabels={
                  Array.isArray(subKey?.customValueLabels) && subKey.customValueLabels.length > 0
                    ? subKey.customValueLabels
                    : userValueLabels
                }
                apiUrl={`/api/users/${caseObj.client.pk}/`}
                customDisplayUrl={
                  Array.isArray(subKey?.customValueLabels) && subKey.customValueLabels.length > 0
                }
                displayDetail={userData}
                {...subKey}
              />
            </div>
          ))}
        </div>
      )),
    [inputs, userValueLabels, userData]
  );

  if (!caseObj || !userRoles.permissions.includes("users.view_user")) {
    return (
      <div className="w-full py-4 pl-4 pr-2">
        <h2 className="my-2 text-center text-gray-500 underline">
          Content details not available for this account at this time
        </h2>
      </div>
    );
  }

  return (
    <div className="w-full py-4 pl-4 pr-2">
      <div className="w-full">{renderFields()}</div>
    </div>
  );
}
